import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "create-ticket-content__scrollable" }
const _hoisted_2 = { class: "create-ticket-content__article" }
const _hoisted_3 = { class: "create-ticket-content__article__details" }
const _hoisted_4 = {
  key: 0,
  class: "create-ticket-content__article",
  style: {"padding-top":"16px"}
}
const _hoisted_5 = { class: "create-ticket-content__article__place-badges" }

import { computed, inject, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useSelectionStore } from '@/pinia/selection'
import { getEntityParents } from '@/utils/entityHelpers'
import {
  getPriorityOptions,
  getStatusOptions,
  translatedPriority,
  translatedStatus
} from '@/utils/ticketHelpers'
import {
  CreateTicket,
  getTicketCategories,
  getTicketTypesForCategory,
  TicketCategory,
  TicketType
} from '@/services/tickets.service'
import { optionType } from '@/design-system/molecules/DropdownStandard.vue'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import DropdownStandard from '@/design-system/molecules/DropdownStandard.vue'
import TextAreaStandard from '@/design-system/atoms/TextAreaStandard.vue'
import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateTicketContent',
  setup(__props) {

const { locale, t } = useI18n()
const store = useStore()

const badgeText = ref<string>('')
const categoryOptions = ref<optionType[]>([])
const priorityOptions = ref<optionType[]>([])
const statusOptions = ref<optionType[]>([])
const typeOptions = ref<optionType[]>([])

const selectionStore = useSelectionStore()
const { clickedEntityType } = storeToRefs(selectionStore)
const selectedObjectId = computed(() => {
  return store.getters.selectedObjectId
})

const formData = inject<CreateTicket>('formDataCreateErrand')
const selectedCategoryId = computed(() => formData?.categoryId)
const selectedCategoryTitle = computed(() => {
  if (!formData || !formData?.categoryId.length)
    return t('general__text__select') + ' ' + t('general__text__category').toLowerCase()
  return categoryOptions.value.find((category) => category.id === formData.categoryId)?.name ?? ''
})
const selectedTypeTitle = computed(() => {
  if (!formData || !formData?.typeId.length)
    return t('general__text__select') + ' ' + t('general__text__type').toLowerCase()
  return typeOptions.value.find((type) => type.id === formData.typeId)?.name ?? ''
})

function addDropdownCategories(ticketCategories: TicketCategory[]) {
  const ticketCategoryOptions = ticketCategories.map((category) => {
    return {
      disabled: false,
      id: category.id,
      name: category.name[locale.value],
      selected: false
    }
  })
  categoryOptions.value = ticketCategoryOptions
}
function addDropdownTypes(typesForCategory: TicketType[]) {
  const ticketTypeOptions = typesForCategory.map((type) => {
    return {
      disabled: false,
      id: type.id,
      name: type.name[locale.value],
      selected: false
    }
  })
  typeOptions.value = ticketTypeOptions
}
function handleDueDateInput(value) {
  if (!formData) return
  formData.dueDate = value + 'T00:00:00.000Z' // Time not used for dueDate according to new design
}
function handleStartDateInput(value) {
  if (!formData) return
  formData.startDate = value + 'T00:00:00.000Z' // Time not used for startDate according to new design
}
function handleDescriptionInput(value) {
  if (!formData) return
  formData.description = value
}
function handleDropdownSelection(property: string, event) {
  if (!formData) return
  formData[property] = event
}
function handleNameInput(value) {
  if (!formData) return
  formData.title = value
}

watch(
  () => formData,
  async (newValue) => {
    if (newValue) {
      priorityOptions.value = getPriorityOptions(newValue.priority)
      statusOptions.value = getStatusOptions(newValue.status)
    }
  },
  { immediate: true }
)
watch(
  () => selectedCategoryId.value,
  async (newValue, oldValue) => {
    if (newValue?.length && newValue !== oldValue) {
      typeOptions.value = []
      if (formData) formData.typeId = ''
      const typesForCategory = await getTicketTypesForCategory(newValue)
      if (typesForCategory?.length) addDropdownTypes(typesForCategory)
    }
  }
)
onMounted(async () => {
  const ticketCategories = await getTicketCategories()
  if (ticketCategories?.length) addDropdownCategories(ticketCategories)
  const entityParents = await getEntityParents(selectedObjectId.value, clickedEntityType.value)
  badgeText.value = entityParents ?? ''
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("article", _hoisted_2, [
      _createVNode(TextFieldStandard, {
        hideIconX: true,
        label: _unref(t)('general__data__ticket-name'),
        prefilledText: _unref(formData)?.title ?? '',
        required: true,
        style: {"margin-bottom":"16px"},
        onEmitInput: handleNameInput
      }, null, 8, ["label", "prefilledText"]),
      _createVNode(TextAreaStandard, {
        label: _unref(t)('description'),
        prefilledText: _unref(formData)?.description ?? '',
        style: {"margin-bottom":"-8px"},
        onEmitInput: handleDescriptionInput
      }, null, 8, ["label", "prefilledText"])
    ]),
    _createElementVNode("article", _hoisted_3, [
      _createVNode(DropdownStandard, {
        class: "create-ticket-content__article__details__dropdown",
        label: _unref(t)('general__text__status'),
        title: _unref(formData)?.status ? _unref(translatedStatus)(_unref(formData).status) : '',
        options: statusOptions.value,
        required: true,
        onSelect: _cache[0] || (_cache[0] = ($event: any) => (handleDropdownSelection('status', $event)))
      }, null, 8, ["label", "title", "options"]),
      _createVNode(DropdownStandard, {
        class: "create-ticket-content__article__details__dropdown",
        label: _unref(t)('general__text__priority'),
        title: _unref(formData)?.priority ? _unref(translatedPriority)(_unref(formData).priority) : '',
        options: priorityOptions.value,
        required: true,
        onSelect: _cache[1] || (_cache[1] = ($event: any) => (handleDropdownSelection('priority', $event)))
      }, null, 8, ["label", "title", "options"]),
      _createVNode(DropdownStandard, {
        class: "create-ticket-content__article__details__dropdown",
        disabled: !categoryOptions.value.length,
        label: _unref(t)('general__text__category'),
        title: selectedCategoryTitle.value,
        options: categoryOptions.value,
        required: true,
        onSelect: _cache[2] || (_cache[2] = ($event: any) => (handleDropdownSelection('categoryId', $event)))
      }, null, 8, ["disabled", "label", "title", "options"]),
      _createVNode(DropdownStandard, {
        class: "create-ticket-content__article__details__dropdown",
        disabled: !typeOptions.value.length,
        label: _unref(t)('general__text__type'),
        title: selectedTypeTitle.value,
        options: typeOptions.value,
        required: true,
        onSelect: _cache[3] || (_cache[3] = ($event: any) => (handleDropdownSelection('typeId', $event)))
      }, null, 8, ["disabled", "label", "title", "options"]),
      _createVNode(TextFieldStandard, {
        hideIconX: true,
        label: _unref(t)('general__text__start-date'),
        prefilledText: 
          _unref(formData)?.startDate ? new Date((_unref(formData)).startDate).toLocaleDateString() : ''
        ,
        type: "date",
        class: "create-ticket-content__article__details__input",
        onEmitInput: handleStartDateInput
      }, null, 8, ["label", "prefilledText"]),
      _createVNode(TextFieldStandard, {
        hideIconX: true,
        label: _unref(t)('general__text__due-date'),
        prefilledText: _unref(formData)?.dueDate ? new Date((_unref(formData)).dueDate).toLocaleDateString() : '',
        type: "date",
        class: "create-ticket-content__article__details__input",
        onEmitInput: handleDueDateInput
      }, null, 8, ["label", "prefilledText"])
    ]),
    (badgeText.value.length)
      ? (_openBlock(), _createElementBlock("article", _hoisted_4, [
          _createElementVNode("span", null, _toDisplayString(_unref(t)('general__text__linked-objects')) + ":", 1),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(BadgeSquare, {
              badgeText: badgeText.value,
              grey: true,
              size: "small"
            }, null, 8, ["badgeText"])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})